<script lang="ts">
  /**
   * The staleness chip (law 10: one on every data panel; law 1: it must be loud).
   *
   * States, and NONE of them is "absent":
   *   live       age within budget                  — quiet, with a dot
   *   CLOSED     over budget, but the MARKET is shut — quiet: a fact about the world, not a fault
   *   REOPENING  session just resumed, first bar not due yet
   *   STALE      over budget during session hours   — amber, bordered, bold, age spelled out
   *   ERROR      the feed itself failed             — amber, reason in the title
   *   NO DATA    nothing received yet
   *
   * WHY CLOSED EXISTS (see session.ts for the measurements): CME halts an hour every weekday and
   * closes 49h every weekend. With one budget and one failure state this chip went red daily for an
   * hour and all weekend — and a signal that cries wolf on schedule is one people stop reading. That
   * is fail-loud theatre, which is worse than no chip. CLOSED is NOT a wider budget: a feed that
   * dies mid-session still reads STALE even if the market later shuts, because session.ts requires
   * the last print to be consistent with the close before it will excuse the silence.
   */
  import { formatAge, subscribeServedVenueState, type VenueState } from "./api";
  import { onMount, untrack } from "svelte";
  import { closedVenueChipInkState } from "./order33State.ts";
  import { servedCvdVenueVerdict } from "./cvd2.ts";
  import { advanceStaleInk, feedState, type StaleInkState } from "./session";

  interface Props {
    label: string;
    ageS: number | null;
    budgetS: number;
    /** Identity of the completed response. Clock-only age updates must not count as polls. */
    pollKey: unknown;
    error?: string | null;
    /** On chart chrome, healthy/calendar states collapse to a labelled dot; alerts stay loud. */
    compactLive?: boolean;
    /** Timestamp of the newest bar. Without it CLOSED cannot be distinguished from a real gap. */
    lastBarMs?: number | null;
    /**
     * How much time `lastBarMs` COVERS — the rung's span for bars, `0` for ticks.
     * ⛔ REQUIRED ON PURPOSE (R497(2)). A bar's timestamp is its bucket's START, so a chip that
     * assumed 1m rendered `STALE — a gap, not the close` on a healthy 12m feed while the 1m chip
     * beside it read `CLOSED`. A default here would silently restore exactly that.
     */
    barSpanMs: number;
    servedVenueState?: string | null;
  }
  let {
    label, ageS, budgetS, pollKey, error = null, lastBarMs = null, barSpanMs, compactLive = false,
    servedVenueState = null,
  }: Props = $props();

  let subscribedVenueState = $state<VenueState | null>(null);
  let relayedVenueState = $derived({
    verdict: servedCvdVenueVerdict(pollKey) ?? subscribedVenueState?.verdict ?? null,
  });
  let effectiveServedVenueState = $derived(servedVenueState ?? relayedVenueState?.verdict ?? null);
  let verdict = $derived(feedState({
    ageS, budgetS, lastBarMs, barSpanMs, error,
    servedVenueState: effectiveServedVenueState,
  }));
  let inkMemory: StaleInkState = { consecutiveOverBudgetPolls: 0, visible: false };
  let staleInkVisible = $state(false);
  $effect(() => {
    void pollKey;
    untrack(() => {
      inkMemory = advanceStaleInk(inkMemory, { rawState: verdict.state, ageS, budgetS });
      staleInkVisible = inkMemory.visible;
    });
  });
  // R1106: only the painted state is damped. data-raw-verdict always exposes feedState truth.
  let inkState = $derived(closedVenueChipInkState(
    verdict.state === "stale" && !staleInkVisible ? "live" : verdict.state,
    effectiveServedVenueState,
  ));
  let reopenIn = $derived(
    verdict.session.reopensAt !== null
      ? formatAge((verdict.session.reopensAt - Date.now()) / 1000)
      : null,
  );
  onMount(() => subscribeServedVenueState((state) => { subscribedVenueState = state; }));
</script>

{#if inkState === "error"}
  <span class="age err" data-raw-verdict={verdict.state} title={error ?? "feed error"}>{label} ERROR</span>
{:else if inkState === "nodata"}
  <span class="age err" data-raw-verdict={verdict.state} title="no data received yet">{label} NO DATA</span>
<!--
  ⚠ EVERY BRANCH CARRIES `label`, INCLUDING THESE THREE — corrected 2026-08-09 (HUDCHAT-f).
  The stale/closed/reopening branches used to drop it. That was invisible while every panel had
  exactly ONE chip: with one chip, "which feed" has only one answer. The ES panel now runs TWO
  feeds behind one header (price 90s, dealer levels 3m), and unlabelled those chips read
  `price 41s` + `STALE 9m 46s` — the reader cannot tell WHICH feed went stale — and on a shut
  market both render the identical string about different sources.
  ⇒ A LABEL THAT IS ONLY REDUNDANT BECAUSE THERE IS ONE OF SOMETHING STOPS BEING REDUNDANT THE
    MOMENT THERE ARE TWO, AND NOTHING WARNS YOU AT THE SEAM.
  Verified against all three call sites: the dealer rail's chip becomes "poll STALE …" and passes
  no lastBarMs, so it cannot reach the closed branches at all.
-->
{:else if compactLive && inkState === "closed"}
  <span
    class="age closed compact"
    data-raw-verdict={verdict.state}
    aria-label={`${label} closed · ${verdict.session.label}`}
    title="{verdict.reason}{reopenIn ? ` · reopens in ${reopenIn}` : ''}"></span>
{:else if compactLive && inkState === "reopening"}
  <span class="age closed compact" data-raw-verdict={verdict.state} aria-label={`${label} reopening`} title={verdict.reason}></span>
{:else if inkState === "closed"}
  <!-- Quiet on purpose. Amber here would be the false alarm this state exists to remove. -->
  <span class="age closed" data-raw-verdict={verdict.state} title="{verdict.reason}{reopenIn ? ` · reopens in ${reopenIn}` : ''}">
    {label} CLOSED · {verdict.session.label}
  </span>
{:else if inkState === "reopening"}
  <span class="age closed" data-raw-verdict={verdict.state} title={verdict.reason}>{label} REOPENING</span>
{:else if inkState === "stale"}
  <span class="age stale" data-raw-verdict={verdict.state} title={verdict.reason}>
    {label} STALE {formatAge(ageS ?? 0)}
  </span>
{:else if inkState === "live" && compactLive}
  <span
    class="age live compact"
    data-raw-verdict={verdict.state}
    aria-label={`${label} fresh · ${formatAge(ageS ?? 0)}`}
    title={`${label} fresh · ${formatAge(ageS ?? 0)}`}></span>
{:else}
  <span class="age live" data-raw-verdict={verdict.state}>{label} {formatAge(ageS ?? 0)}</span>
{/if}

<style>
  .age {
    margin-left: auto;
    font-size: 10px;
    letter-spacing: 0.06em;
    color: var(--muted);
    background: var(--chip);
    border-radius: 5px;
    padding: 2px 7px;
    white-space: nowrap;
  }
  .age.live::before {
    content: "● ";
    color: var(--up);
  }
  .age.compact {
    box-sizing: border-box;
    width: 12px;
    height: 12px;
    margin-left: 0;
    padding: 0;
    border-radius: 999px;
  }
  .age.compact::before {
    display: block;
    width: 100%;
    text-align: center;
    font-size: 8px;
    line-height: 10px;
  }
  /* Loud by construction: colour, border AND weight all change — not colour alone,
     which would vanish for a colour-blind reader. */
  /* CLOSED is a fact about the world, not a fault: it reads as information, not alarm. It is still
     bordered and labelled so it can never be mistaken for a live chip. */
  .age.closed {
    color: var(--muted);
    background: var(--surface-2);
    border: 1px solid var(--line);
    font-weight: 600;
  }
  .age.closed::before {
    content: "◦ ";
    color: var(--faint);
  }
  .age.stale,
  .age.err {
    /* --stale-ink, NOT --stale: the palette receipt measured --stale on --stale-soft
       at 3.14:1, which fails AA for 10px text. --stale stays the border/rule colour. */
    color: var(--stale-ink);
    background: var(--stale-soft);
    border: 1px solid var(--stale);
    font-weight: 700;
  }
</style>
